import React, { useMemo, useState } from 'react';
import { buildReorderPlan, REORDER_DEFAULTS } from './skuReorderEngine';
import { addDays, fmtDate, GROWTH_SOURCE_LABEL } from './skuFamilyLiveEngine';

// Per-SKU factory reorder plan — see skuReorderEngine.js for the math.
// All sfl- classes live in SkuFamilyLiveInvPage.css.

const STATUS_COLOR = { 'Order now': '#f87171', 'Order soon': '#fbbf24', OK: '#4ade80', 'No sales': '#64748b' };
const SERVICE_LEVELS = [
  { z: 1.28, label: '90%' },
  { z: 1.65, label: '95%' },
  { z: 2.05, label: '98%' },
];

const pct = r => {
  const v = Math.round(r * 100) || 0; // || 0 turns -0 into 0, so no "-0%"
  return `${v >= 0 ? '+' : ''}${v}%`;
};
const pctOr = r => (r == null ? '' : pct(r));
const num = v => Math.round(v).toLocaleString();
const perDay = v => v.toFixed(v < 10 ? 2 : 1);
const runoutDate = (r, asOf) => (r.stockoutDay != null ? fmtDate(addDays(asOf, r.stockoutDay)) : '');

function toCsv(rows, asOf) {
  const header = ['Status', 'SKU', 'Family', 'Product', 'Stock', 'Incoming',
    'D2C/day', 'D2C growth', 'D2C growth source', 'POS/day', 'POS growth', 'POS growth source', 'B2B/day', 'B2B growth', 'B2B growth source',
    'Total demand/day', 'Combined growth (1 yr)', 'Demand basis', 'Runs out (no new order)',
    'Months of supply', 'Safety stock', 'Order by', 'Reorder qty', 'Master cartons', 'Carton size', 'Stock-out before arrival'];
  const src = ch => GROWTH_SOURCE_LABEL[ch.source] + (ch.sourceKey ? ` (${ch.sourceKey})` : '');
  const esc = v => (v == null ? '' : /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
  const lines = rows.map(r => [
    r.status, r.sku, r.family, r.title || r.color, Math.round(r.stock), r.incomingTotal,
    r.fc.d2c.today.toFixed(2), pct(r.fc.d2c.rate), src(r.fc.d2c),
    r.fc.pos.today.toFixed(2), pct(r.fc.pos.rate), src(r.fc.pos), r.fc.b2b.today.toFixed(2), pct(r.fc.b2b.rate), src(r.fc.b2b),
    r.rate.toFixed(2), pctOr(r.fc.rate), r.rateBasis, runoutDate(r, asOf), r.monthsOfSupply != null ? r.monthsOfSupply.toFixed(1) : '',
    r.safety, r.orderByDay != null ? fmtDate(addDays(asOf, r.orderByDay)) : '', r.qty || '', r.cartons ?? '',
    r.masterQty ?? '', r.gapBeforeArrival ? 'yes' : '',
  ].map(esc).join(','));
  return [header.join(','), ...lines].join('\n');
}

// Tooltip for one channel's growth cell: where the rate came from, and the
// SKU's own daily rates in that channel.
function channelTitle(r, key, server) {
  const ch = r.fc[key];
  const own = r.yoy?.forecast?.channels[server];
  let t = GROWTH_SOURCE_LABEL[ch.source] + (ch.sourceKey ? ` (${ch.sourceKey})` : '');
  if (own?.last_year_per_day != null) {
    t += `\nThis SKU: ${own.last_year_per_day.toFixed(2)}/day last yr → ${own.this_year_per_day.toFixed(2)}/day this yr`;
  }
  return t;
}

export default function SkuReorderPlan({ skus, asOf, asOfStr, skuGrowthData, growthData, restockEvents, restockPlanName }) {
  const [leadTimeDays, setLeadTimeDays] = useState(REORDER_DEFAULTS.leadTimeDays);
  const [coverageDays, setCoverageDays] = useState(REORDER_DEFAULTS.coverageDays);
  const [serviceZ, setServiceZ] = useState(REORDER_DEFAULTS.serviceZ);
  const [family, setFamily] = useState('');
  const [needsOrderOnly, setNeedsOrderOnly] = useState(true);

  const plan = useMemo(() => buildReorderPlan(skus, {
    skuGrowthData, growthData, restockEvents, asOf, leadTimeDays, coverageDays, serviceZ,
  }), [skus, skuGrowthData, growthData, restockEvents, asOf, leadTimeDays, coverageDays, serviceZ]);

  const families = useMemo(() => [...new Set(plan.map(r => r.family))].sort(), [plan]);
  const rows = plan.filter(r => (!family || r.family === family)
    && (!needsOrderOnly || r.status === 'Order now' || r.status === 'Order soon'));
  const totalQty = rows.reduce((a, r) => a + (r.status === 'Order now' || r.status === 'Order soon' ? r.qty : 0), 0);
  const counts = plan.reduce((c, r) => ({ ...c, [r.status]: (c[r.status] || 0) + 1 }), {});

  function downloadCsv() {
    const blob = new Blob([toCsv(rows, asOf)], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `reorder_plan_${asOfStr}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <section className="sfl-section sfl-reorder">
      <div className="sfl-reorder-settings">
        <label>Lead time (days)
          <input type="number" className="sfl-num" value={leadTimeDays}
            onChange={e => setLeadTimeDays(Math.max(0, parseInt(e.target.value, 10) || 0))} />
        </label>
        <label>Order covers (days)
          <input type="number" className="sfl-num" value={coverageDays}
            onChange={e => setCoverageDays(Math.max(1, parseInt(e.target.value, 10) || 1))} />
        </label>
        <label>Service level
          <select className="sfl-select" value={serviceZ} onChange={e => setServiceZ(parseFloat(e.target.value))}>
            {SERVICE_LEVELS.map(l => <option key={l.z} value={l.z}>{l.label}</option>)}
          </select>
        </label>
        <label>Family
          <select className="sfl-select" value={family} onChange={e => setFamily(e.target.value)}>
            <option value="">All</option>
            {families.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </label>
        <label className="sfl-check">
          <input type="checkbox" checked={needsOrderOnly} onChange={e => setNeedsOrderOnly(e.target.checked)} />
          Only SKUs to order in the next 30 days
        </label>
        <button type="button" className="sfl-btn-secondary" onClick={downloadCsv}>Download CSV</button>
      </div>

      <p className="sfl-muted">
        <strong style={{ color: STATUS_COLOR['Order now'] }}>{counts['Order now'] || 0} order now</strong> ·{' '}
        <strong style={{ color: STATUS_COLOR['Order soon'] }}>{counts['Order soon'] || 0} order soon</strong> ·{' '}
        {counts.OK || 0} OK · {counts['No sales'] || 0} no sales
        {totalQty > 0 && <> · <strong>{num(totalQty)} units</strong> to order in this view</>}
        {' '}· incoming from restock plan: {restockPlanName || 'none'}
      </p>

      <div className="sfl-reorder-wrap">
        <table className="sfl-reorder-table">
          <thead>
            <tr>
              <th>Status</th><th>SKU</th><th>Product</th>
              <th title="On hand now">Stock</th>
              <th title="Total in the selected restock plan">Incoming</th>
              <th title="D2C (web store) demand per day today">D2C/day</th>
              <th title="D2C growth: units per day on sale, last 12 months vs the 12 before. Dimmed = inherited from the family or category">D2C gr.</th>
              <th title="POS (convention booth) demand per day today">POS/day</th>
              <th title="POS growth: units per day on sale, last 12 months vs the 12 before. Dimmed = inherited from the family or category">POS gr.</th>
              <th title="B2B (distributor + wholesale, DIST/WHSL) demand per day today">B2B/day</th>
              <th title="B2B growth: units per day on sale, last 12 months vs the 12 before. Dimmed = inherited from the family or category">B2B gr.</th>
              <th title="D2C + POS + B2B demand per day today — hover for how it was worked out">Total/day</th>
              <th title="Change in total daily demand over the next year, from all three channels' rates">Growth</th>
              <th title="Projected stock-out with no new factory order (restock plan included)">Runs out</th>
              <th title="(Stock + incoming) / current demand">Mo. supply</th>
              <th title="Buffer from sales volatility at the chosen service level (max 90 days of demand)">Safety</th>
              <th>Order by</th>
              <th title={`Covers ${coverageDays} days after the order lands, plus safety stock, rounded up to master cartons`}>Reorder qty</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.sku}>
                <td style={{ color: STATUS_COLOR[r.status], whiteSpace: 'nowrap' }}>
                  {r.status}
                  {r.gapBeforeArrival && (
                    <span className="sfl-gap-flag" title={`Runs out before an order placed today could land (${leadTimeDays} days)`}> ⚠ gap</span>
                  )}
                </td>
                <td className="sfl-mono">{r.sku}</td>
                <td>{r.title || r.color}</td>
                <td>{num(r.stock)}</td>
                <td title={r.nextIncoming ? `next: ${r.nextIncoming}` : ''}>{r.incomingTotal ? num(r.incomingTotal) : ''}</td>
                <td>{perDay(r.fc.d2c.today)}</td>
                <td className={r.fc.d2c.source === 'sku' ? '' : 'sfl-dim'} title={channelTitle(r, 'd2c', 'd2c')}>{pct(r.fc.d2c.rate)}</td>
                <td>{perDay(r.fc.pos.today)}</td>
                <td className={r.fc.pos.source === 'sku' ? '' : 'sfl-dim'} title={channelTitle(r, 'pos', 'pos')}>{pct(r.fc.pos.rate)}</td>
                <td>{perDay(r.fc.b2b.today)}</td>
                <td className={r.fc.b2b.source === 'sku' ? '' : 'sfl-dim'} title={channelTitle(r, 'b2b', 'wholesale')}>{pct(r.fc.b2b.rate)}</td>
                <td title={`${r.rateBasis}\n180-day rate: ${r.rawRate.toFixed(1)}/day`}
                  className={Math.abs(r.rate - r.rawRate) > 0.2 * Math.max(r.rawRate, 0.1) ? 'sfl-adjusted' : ''}>
                  <strong>{perDay(r.rate)}</strong>
                </td>
                <td>{r.fc.rate != null ? pct(r.fc.rate) : '—'}</td>
                <td>{runoutDate(r, asOf) || '—'}</td>
                <td>{r.monthsOfSupply != null ? r.monthsOfSupply.toFixed(1) : '—'}</td>
                <td title={r.safetyBasis}>{num(r.safety)}</td>
                <td>{r.orderByDay != null ? fmtDate(addDays(asOf, r.orderByDay)) : '—'}</td>
                <td title={r.cartons ? `${r.cartons} × ${r.masterQty}` : 'no master carton size on file — not rounded'}>
                  {r.qty ? <strong>{num(r.qty)}</strong> : '—'}
                  {r.cartons ? <span className="sfl-dim"> ({r.cartons} ctn)</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="sfl-muted">Nothing to order in this view.</p>}
      </div>
      <p className="sfl-hint">
        D2C and B2B are forecast separately — each channel's last-12-months daily rate (days in stock only),
        brought forward to today and grown at that channel's own rate for 12 months, then flat — and added day by
        day for the run-out date and reorder qty. Orange total = more than 20% off the plain 180-day rate (a
        distributor PO in or out of the last 6 months, or stock-outs) — hover for both. ⚠ gap = it runs out before
        an order placed today could land. Reorder qty assumes the order is placed on its order-by date.
      </p>
    </section>
  );
}
